// Language tests — shared frontend vocabulary.
//
// This MIRRORS backend/language_tests.py. The server is the source of truth and
// revalidates everything; this file only drives the UI so we never offer a
// status, a level or a scoring model the backend would reject.
//
// The one idea worth carrying over intact: the two tests are different
// instruments. English is pass/fail at 70%; French places a candidate on the
// CEFR scale and has no pass mark. Anywhere a result is rendered, `scoring`
// decides which sentence is true — never a check for "is there a level".

export const TEST_STATUS_META = {
  invited: { label: 'Invited', pillTone: 'neutral' },
  in_progress: { label: 'In progress', pillTone: 'gold' },
  submitted: { label: 'Submitted', pillTone: 'emerald' },
  expired: { label: 'Expired', pillTone: 'slate' },
};

export const TEST_STATUSES = Object.keys(TEST_STATUS_META);

export const TARGET_TYPE_META = {
  student: { label: 'Student', path: 'students' },
  application: { label: 'Applicant', path: 'applications' },
  standalone: { label: 'Not in the portal', path: '' },
};

export const DELIVERY_STATUS_LABELS = {
  link_created: 'Link created',
  emailed: 'Emailed',
  email_failed: 'Email failed',
};

export const RESULT_EMAIL_LABELS = {
  emailed: 'Result emailed',
  email_failed: 'Result email failed',
  link_created: 'Not emailed',
  '': '—',
};

export function testStatusMeta(status) {
  return TEST_STATUS_META[status] || { label: status || 'Unknown', pillTone: 'neutral' };
}

export function targetTypeMeta(targetType) {
  return TARGET_TYPE_META[targetType] || { label: targetType || '—', path: '' };
}

/** Where a result's linked profile lives, or '' when it is linked to nobody. */
export function profilePathFor(row) {
  const type = row?.linked_type;
  if (type === 'student') return `/admin/students/${row.linked_id}`;
  if (type === 'application') return `/admin/applications/${row.linked_id}`;
  return '';
}

/**
 * The one line that states a result, and the only place that decides how.
 *
 * A pass/fail test reads "21/25 (84%) · Pass"; a placement test reads
 * "31/40 · B1" and never claims a verdict it does not have.
 */
export function resultText(row) {
  if (!row || row.status !== 'submitted') return '';
  const score = `${row.score}/${row.max_score}`;
  if (row.scoring === 'placement') {
    return `${score} · ${row.level_label || '—'}`;
  }
  return `${score} (${row.percentage}%) · ${row.passed ? 'Pass' : 'Not passed'}`;
}

/**
 * How a result should read visually: 'good', 'warn' or 'neutral'.
 *
 * A placement test has no bad outcome — being placed at A1 is information, not
 * a failure — so it is never rendered in a warning colour. Only a pass/fail
 * test can be "not passed".
 */
export function resultTone(row) {
  if (!row || row.status !== 'submitted') return 'neutral';
  if (row.scoring === 'placement') return 'neutral';
  return row.passed ? 'good' : 'warn';
}

/** The levels a placement test can be moved to, for the amend dialog. */
export function levelOptions(test) {
  return (test?.bands || []).map((band) => ({
    value: band.level,
    label: band.label,
    description: band.description,
  }));
}

export function findTest(catalog, code) {
  return (catalog || []).find((test) => test.code === code) || null;
}

/** How many addresses may be copied. Mirrors MAX_COPY_RECIPIENTS. */
export const MAX_COPY_RECIPIENTS = 10;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Turn one typed box into a copy list, the way the backend does.
 *
 * Staff get a single field and can separate addresses however they naturally
 * would — commas, semicolons or spaces. Mirrored here so the dialog can show
 * what will actually be saved; the server normalizes again regardless.
 */
export function parseEmailList(value) {
  const items = Array.isArray(value) ? value : String(value ?? '').split(/[,;\s]+/);
  const cleaned = [];
  items.forEach((item) => {
    const email = String(item ?? '').trim().toLowerCase();
    if (email && EMAIL_RE.test(email) && !cleaned.includes(email)) cleaned.push(email);
  });
  return cleaned.slice(0, MAX_COPY_RECIPIENTS);
}

/** Which typed entries are not usable addresses, for an inline warning. */
export function invalidEmails(value) {
  const items = Array.isArray(value) ? value : String(value ?? '').split(/[,;\s]+/);
  return items
    .map((item) => String(item ?? '').trim())
    .filter((item) => item && !EMAIL_RE.test(item.toLowerCase()));
}

/** A stored copy list back in the one-box form, for editing. */
export function joinEmailList(value) {
  return (Array.isArray(value) ? value : []).join(', ');
}

/** "2 copied" / "1 copied, 1 blind" — what the panel shows at a glance. */
export function copySummary(row) {
  const cc = (row?.cc || []).length;
  const bcc = (row?.bcc || []).length;
  if (!cc && !bcc) return '';
  const parts = [];
  if (cc) parts.push(`${cc} copied`);
  if (bcc) parts.push(`${bcc} blind`);
  return parts.join(', ');
}

export function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

export function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}
